// Cart service for managing user cart

import { api } from '@/api/client';
import { logger } from '@/utils/logger';
import type {
  CartData,
  CartResponse,
  AddToCartRequest,
  CartPlanOption,
} from '@/types';

export async function getCart(userId: string): Promise<CartData | null> {
  const response = await api.get<CartData>('/api/2turniton/cart', { userId });

  if (response.success) {
    return response.data ?? null;
  }

  // 404 means no cart exists yet — a normal empty-cart state.
  if (response.error === 'Not found') {
    return null;
  }

  // Any other failure (5xx, network) is NOT an empty cart. Throw so callers don't
  // mistake a transient read error for "cart is empty" and re-add everything.
  throw new Error(response.error || 'Failed to load cart');
}

export async function addPlanToCart(
  userId: string,
  request: AddToCartRequest
): Promise<CartResponse> {
  const response = await api.post<CartData>(
    '/api/2turniton/cart/plans',
    request,
    { userId }
  );

  return {
    success: response.success,
    data: response.data,
    error: response.error,
  };
}

export async function removePlanFromCart(
  userId: string,
  planId: string
): Promise<CartResponse> {
  // Guard against undefined/empty planId - likely a data mismatch issue
  if (!planId) {
    logger.warn({ area: 'cart' }, 'Attempted to remove plan with invalid planId: ' + planId);
    return { success: false, error: 'Invalid planId' };
  }

  const response = await api.delete<CartData>(
    `/api/2turniton/cart/plans/${planId}`,
    { userId }
  );

  return {
    success: response.success,
    data: response.data,
    error: response.error,
  };
}

export async function clearCart(userId: string): Promise<boolean> {
  const response = await api.delete('/api/2turniton/cart', { userId });
  return response.success;
}

export async function syncCartWithSelections(
  userId: string,
  selections: Array<{
    planId: string;
    vendorId: string;
    serviceName: string;
    planOptions?: CartPlanOption[];
  }>
): Promise<void> {
  // If the current cart can't be read, we don't know the server state — skip the sync
  // rather than re-adding every plan onto an unknown cart (which would duplicate items).
  // Best-effort and non-blocking: the order payload is assembled from store state anyway.
  let currentCart: CartData | null;
  try {
    currentCart = await getCart(userId);
  } catch (err) {
    logger.error({ area: 'cart', userId }, 'Skipping cart sync — failed to read current cart', err instanceof Error ? err : undefined);
    return;
  }

  // Note: Server returns plans with 'id', but frontend uses 'planId' - handle both
  const currentPlanIds = new Set(
    currentCart?.selectedPlans?.map((p) => p.planId || p.id) || []
  );
  const newPlanIds = new Set(selections.map((s) => s.planId));

  // Remove plans no longer selected
  for (const plan of currentCart?.selectedPlans || []) {
    const planIdToRemove = plan.planId || plan.id;
    if (planIdToRemove && !newPlanIds.has(planIdToRemove)) {
      await removePlanFromCart(userId, planIdToRemove);
    }
  }

  // Add new plans
  for (const selection of selections) {
    if (!currentPlanIds.has(selection.planId)) {
      await addPlanToCart(userId, {
        planId: selection.planId,
        vendorId: selection.vendorId,
        serviceName: selection.serviceName,
        planOptions: selection.planOptions,
      });
    }
  }
}
